import React, { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { analytics } from "../../services/analytics";

/**
 * AnalyticsTracker component
 *
 * This component should be placed inside the Router context to handle
 * page load time tracking.
 *
 * It doesn't render any UI - it only handles the analytics side effects.
 */
const AnalyticsTracker: React.FC = () => {
  const location = useLocation();
  const previousPathRef = useRef<string>("");
  const renderStartTimeRef = useRef<number>(0);

  useEffect(() => {
    // Guard clause - only run in browser environment
    if (
      typeof window === "undefined" ||
      typeof requestAnimationFrame === "undefined" ||
      typeof cancelAnimationFrame === "undefined"
    ) {
      return () => {};
    }

    const currentPath = location.pathname;

    // Only track if path has changed
    if (currentPath === previousPathRef.current) {
      return () => {};
    }

    // Start timing when route changes
    analytics.startPageLoadTiming();
    renderStartTimeRef.current = window.performance.now();

    // Track the page view after a short delay to allow for render completion
    const trackPageView = () => {
      const renderTime = window.performance.now() - renderStartTimeRef.current;
      analytics.endPageLoadTimingAndTrack(currentPath, renderTime);
      previousPathRef.current = currentPath;
    };

    // Use requestAnimationFrame to ensure rendering is complete
    // eslint-disable-next-line no-undef
    const rafId = requestAnimationFrame(() => {
      // Additional delay to account for any async operations
      setTimeout(trackPageView, 100);
    });

    return () => {
      // eslint-disable-next-line no-undef
      cancelAnimationFrame(rafId);
    };
  }, [location.pathname]);

  return null; // This component doesn't render anything
};

export default AnalyticsTracker;
